import {inject, Injectable} from '@angular/core';
import {Auth} from '@angular/fire/auth';
import {environment} from '../../../../environments/environment';
import {GALLIX_MICROSOFT_MAIL_MESSAGE} from '../models/email/microsoft-mail.utils';

export const MICROSOFT_MAIL_WINDOW_TIMEOUT_MS = 60_000;

export interface IMicrosoftMailPopupResult {
  status: 'connected' | 'admin-consented' | 'error' | 'cancelled';
  email?: string;
  message?: string;
}

interface IMicrosoftConnectResponse {
  adminConsentUrl: string;
  authorizeUrl: string;
}

/**
 * Talks to the Gallix emailer for Microsoft 365 mailbox connect, admin consent, and disconnect.
 * The Firebase ID token is sent as a bearer token. The body userId is the Gallix user email.
 */
@Injectable({
  providedIn: 'root',
})
export class MicrosoftMailService {
  private readonly auth = inject(Auth);
  #stopListening: (() => void) | null = null;
  #closePopup: (() => void) | null = null;

  /**
   * Closes the Microsoft window after Gallix already has the result from the saved mailbox.
   */
  dismissPopup(): void {
    this.#closePopup?.();
  }

  /**
   * Starts Microsoft sign-in and resolves when the emailer posts the result.
   */
  async connect(storeId: string, gallixUserId: string, loginHint?: string): Promise<IMicrosoftMailPopupResult> {
    const urls = await this.requestConnectUrls(storeId, gallixUserId, loginHint);
    return this.openPopup(urls.authorizeUrl);
  }

  /**
   * Opens the one-time organisation approval page for a Microsoft 365 admin.
   */
  async requestAdminConsent(storeId: string, gallixUserId: string, loginHint?: string): Promise<IMicrosoftMailPopupResult> {
    const urls = await this.requestConnectUrls(storeId, gallixUserId, loginHint);
    return this.openPopup(urls.adminConsentUrl);
  }

  /**
   * Removes the Microsoft 365 connection for the signed-in user and store.
   */
  async disconnect(storeId: string, gallixUserId: string): Promise<void> {
    await this.authorizedPost('/microsoft/disconnect', {
      userId: gallixUserId,
      storeId,
    });
  }

  /**
   * Posts the connect request and returns the authorize and admin-consent URLs.
   * Local Gallix uses this page's address, and falls back to the configured site if the emailer rejects it.
   */
  private async requestConnectUrls(
    storeId: string,
    gallixUserId: string,
    loginHint?: string,
  ): Promise<IMicrosoftConnectResponse> {
    const current = this.returnUrl();
    try {
      return await this.postConnect(storeId, gallixUserId, loginHint, current);
    } catch (error) {
      const fallback = this.configuredReturnUrl();
      const rejected = error instanceof Error && error.message.includes('Return address is not allowed');
      if (!rejected || current === fallback) {
        throw error;
      }
      return await this.postConnect(storeId, gallixUserId, loginHint, fallback);
    }
  }

  /**
   * Sends one connect request for a return address.
   */
  private async postConnect(
    storeId: string,
    gallixUserId: string,
    loginHint: string | undefined,
    returnUrl: string,
  ): Promise<IMicrosoftConnectResponse> {
    const body: {userId: string; storeId: string; returnUrl: string; loginHint?: string} = {
      userId: gallixUserId,
      storeId,
      returnUrl,
    };
    const hint = loginHint?.trim();
    if (hint) {
      body.loginHint = hint;
    }
    const response = await this.authorizedPost<Partial<IMicrosoftConnectResponse>>('/microsoft/connect', body);
    if (typeof response.authorizeUrl !== 'string' || typeof response.adminConsentUrl !== 'string') {
      throw new Error('Microsoft 365 did not return a sign-in link.');
    }
    return {
      authorizeUrl: response.authorizeUrl,
      adminConsentUrl: response.adminConsentUrl,
    };
  }

  /**
   * Return URL on this Gallix page. The emailer posts the result back to that origin.
   */
  private returnUrl(): string {
    const origins = this.configuredSiteOrigins();
    const current = window.location.origin.replace(/\/$/, '');
    const useCurrent = origins.includes(current)
      || current.startsWith('http://localhost:')
      || current.startsWith('http://127.0.0.1:');
    return this.returnUrlFor(useCurrent && current ? current : origins[0] ?? '');
  }

  /**
   * Configured Gallix site, used when this page's address is not on the emailer allow list.
   */
  private configuredReturnUrl(): string {
    return this.returnUrlFor(this.configuredSiteOrigins()[0] ?? '');
  }

  /**
   * Comma-separated Gallix origins that may receive the Microsoft result.
   */
  private configuredSiteOrigins(): string[] {
    return environment.microsoftMail.siteOrigin
      .split(',')
      .map((origin) => origin.trim().replace(/\/$/, ''))
      .filter((origin) => origin.length > 0);
  }

  /**
   * Joins an origin with the Microsoft return path.
   */
  private returnUrlFor(siteOrigin: string): string {
    const returnPath = environment.microsoftMail.returnPath.startsWith('/')
      ? environment.microsoftMail.returnPath
      : `/${environment.microsoftMail.returnPath}`;
    return `${siteOrigin}${returnPath}`;
  }

  /**
   * Sends a bearer-authenticated POST to the emailer.
   */
  private async authorizedPost<T>(path: string, body: unknown): Promise<T> {
    const origin = environment.microsoftMail.emailerOrigin.replace(/\/$/, '');
    if (!origin) {
      throw new Error('Microsoft 365 is not configured for this environment.');
    }
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) {
      throw new Error('Sign in before using Microsoft 365.');
    }
    const idToken = await firebaseUser.getIdToken();
    const response = await fetch(`${origin}${path}`, {
      method: 'POST',
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: `Bearer ${idToken}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      let detail = '';
      try {
        const body = await response.json() as {message?: unknown};
        if (typeof body.message === 'string' && body.message.trim()) {
          detail = ` ${body.message.trim()}`;
        }
      } catch {
        detail = '';
      }
      throw new Error(`Microsoft 365 request failed (${response.status}).${detail}`);
    }
    return await response.json() as T;
  }

  /**
   * Opens the emailer URL and accepts a window message only from that origin.
   * Microsoft's sign-in page blocks popup.closed, so the result comes from the message instead.
   */
  private openPopup(url: string): Promise<IMicrosoftMailPopupResult> {
    this.#stopListening?.();
    const popup = window.open(url, 'gallix-microsoft-mail', 'popup=yes,width=520,height=720');
    if (!popup) {
      return Promise.reject(new Error('Allow pop-ups to continue with Microsoft 365.'));
    }
    const emailerOrigin = environment.microsoftMail.emailerOrigin.replace(/\/$/, '');
    const channel = new BroadcastChannel(GALLIX_MICROSOFT_MAIL_MESSAGE);
    return new Promise((resolve) => {
      let settled = false;
      const timeoutId = window.setTimeout(() => {
        finish({status: 'cancelled'});
      }, MICROSOFT_MAIL_WINDOW_TIMEOUT_MS);

      const release = (): void => {
        window.clearTimeout(timeoutId);
        window.removeEventListener('message', onMessage);
        channel.close();
        this.#stopListening = null;
        this.#closePopup = null;
        try {
          popup.close();
        } catch {
          // Microsoft's page can sever the handle to the popup.
        }
      };

      const finish = (result: IMicrosoftMailPopupResult): void => {
        if (settled) {
          return;
        }
        settled = true;
        release();
        resolve(result);
      };

      const onMessage = (event: MessageEvent): void => {
        if (event.origin !== emailerOrigin && event.origin !== window.location.origin) {
          return;
        }
        const result = readMicrosoftMailMessage(event.data);
        if (!result) {
          return;
        }
        finish(result);
      };

      channel.onmessage = (event: MessageEvent): void => {
        const result = readMicrosoftMailMessage(event.data);
        if (!result) {
          return;
        }
        finish(result);
      };

      this.#closePopup = (): void => {
        if (settled) {
          return;
        }
        settled = true;
        release();
        resolve({status: 'cancelled'});
      };
      this.#stopListening = (): void => {
        finish({status: 'cancelled'});
      }
      window.addEventListener('message', onMessage);
    });
  }
}

/**
 * Reads the emailer popup payload. Any other fields, including tokens, are ignored.
 */
function readMicrosoftMailMessage(data: unknown): IMicrosoftMailPopupResult | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const record = data as Record<string, unknown>;
  if (record['type'] !== GALLIX_MICROSOFT_MAIL_MESSAGE) {
    return null;
  }
  const status = record['status'];
  if (status !== 'connected' && status !== 'admin-consented' && status !== 'error') {
    return null;
  }
  return {
    status,
    email: typeof record['email'] === 'string' ? record['email'] : undefined,
    message: typeof record['message'] === 'string' ? record['message'] : undefined,
  };
}
